/**
 * IngredientPickerModal — aranabilir hammadde seçici modal.
 *
 * Reçeteye malzeme eklerken hammadde bu modaldan seçilir. Arama
 * SERVER-SIDE'dır (debounced → /ingredients/?search=...): binlerce hammadde
 * olsa bile DOM'a yalnız eşleşen sayfa kadar satır basılır — düz bir
 * Select gibi tüm listeyi yüklemez.
 *
 * `excludeIds` ile reçetede zaten olan hammaddeler gizlenir.
 */
import { useState } from "react";
import {
  Badge, Box, Button, Group, Loader, Modal, ScrollArea, Stack, Text, TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteResource } from "../../../shared/api";
import { MoneyDisplay, useInfiniteScrollRef } from "../../../shared/ui";

interface Ingredient {
  id: string;
  name: string;
  sku: string;
  unit: string;
  last_cost_minor: number;
  is_active: boolean;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Seçilen hammadde — modal kapanır. */
  onSelect: (ingredient: Ingredient) => void;
  /** Listeden gizlenecek hammadde id'leri (reçetede zaten olanlar). */
  excludeIds?: string[];
}

export function IngredientPickerModal({ opened, onClose, onSelect, excludeIds = [] }: Props) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  const {
    items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<Ingredient>("ingredients", { search: debounced });

  const excluded = new Set(excludeIds);
  const visible = items.filter((i) => !excluded.has(i.id));

  // ScrollArea'nın İÇ viewport'u — sentinel observer'ının root'u. Bu olmadan
  // observer tarayıcı viewport'unu ölçer; sentinel 420px'lik iç kapta her
  // zaman "görünür" sayılır → spinner sonsuz döner (bkz. useInfiniteScrollRef).
  // useRef DEĞİL state: viewport mount olunca (null→element) re-render tetiklenip
  // observer gerçek root'la yeniden kurulsun; ref.current sessizce değişip
  // effect'i tetiklemezdi.
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    itemCount: items.length,
    root: viewport,
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Hammadde Seç" size="md">
      <Stack gap="sm">
        <Text size="xs" c="dimmed">Reçeteye eklemek için hammadde arayın ve seçin</Text>
        <TextInput
          placeholder="Hammadde adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />

        {/* SABİT yükseklik (Autosize DEĞİL): yeni sayfa eklendikçe kabın
            büyümesini ve modalın Y'de zıplamasını önler. offsetScrollbars:
            scrollbar için her zaman yer ayır — belirip kaybolurken yatay
            kayma olmasın, kart içeriğinin ("/ gr") üstüne binmesin. */}
        <ScrollArea h={420} offsetScrollbars viewportRef={setViewport}>
          {isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {debounced ? "Eşleşen hammadde yok" : "Henüz hammadde yok"}
            </Text>
          ) : (
            <Stack gap={6}>
              {visible.map((i) => (
                <UnstyledButton
                  key={i.id}
                  onClick={() => { onSelect(i); onClose(); }}
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: 10,
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Box style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" truncate>{i.name}</Text>
                      <Text size="xs" c="dimmed" truncate>
                        SKU: {i.sku || "-"} · Birim: {i.unit}
                      </Text>
                    </Box>
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text size="sm" c="dimmed">
                        <MoneyDisplay minor={i.last_cost_minor} />
                        <Text span size="xs"> / {i.unit}</Text>
                      </Text>
                      {!i.is_active && <Badge color="gray" variant="light" size="sm">pasif</Badge>}
                    </Group>
                  </Group>
                </UnstyledButton>
              ))}
              {/* Infinite-scroll sentinel — görününce sonraki sayfa çekilir */}
              {hasNextPage && (
                <Box ref={sentinelRef}>
                  <Group justify="center" py="sm"><Loader size="xs" /></Group>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
